import { useCallback, useEffect, useState } from 'react';
import { householdService, userService } from '@/services';

/** Approved-email count at which we surface the one-time feedback prompt. */
export const FEEDBACK_APPROVAL_THRESHOLD = 5;

export interface UseFeedbackPromptOptions {
  householdId: string | null | undefined;
  /**
   * Gate the evaluation — e.g. don't compete with the aha walkthrough.
   * When false the hook does nothing (no fetch, never opens). Default true.
   */
  enabled?: boolean;
}

export interface UseFeedbackPromptResult {
  open: boolean;
  onClose: () => void;
}

/**
 * Opens the feedback modal once, the first time a user lands in the app after
 * their household has approved at least {@link FEEDBACK_APPROVAL_THRESHOLD}
 * emails (lifetime). "Once per user" is persisted via
 * `users.feedback_prompt_seen_at` so it never auto-opens again on any device.
 *
 * The flag is written the moment we decide to show it (not on close), so a
 * user who dismisses without submitting still won't be prompted again.
 */
export function useFeedbackPrompt({
  householdId,
  enabled = true,
}: UseFeedbackPromptOptions): UseFeedbackPromptResult {
  const [open, setOpen] = useState(false);
  // Evaluate at most once per mount, regardless of re-renders.
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!enabled || !householdId || resolved) return;
    let cancelled = false;

    (async () => {
      try {
        const profile = await userService.getProfile();
        if (cancelled) return;
        // Already shown once → never auto-open again.
        if (!profile || profile.feedback_prompt_seen_at) {
          setResolved(true);
          return;
        }

        const approved =
          await householdService.getLifetimeEmailsApproved(householdId);
        if (cancelled) return;

        if (approved >= FEEDBACK_APPROVAL_THRESHOLD) {
          setOpen(true);
          // Persist immediately so a close-without-submit doesn't re-trigger.
          // Fire-and-forget: a failed write at worst shows the prompt again
          // next session, which is acceptable.
          void userService
            .updateProfile({
              feedback_prompt_seen_at: new Date().toISOString(),
            })
            .catch(() => {
              /* non-fatal */
            });
        }
        setResolved(true);
      } catch {
        if (!cancelled) setResolved(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, householdId, resolved]);

  const onClose = useCallback(() => setOpen(false), []);

  return { open, onClose };
}
